import { Box, withStyles, getCSSVariable } from 'vopay';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, subDays, subMonths } from 'date-fns';
import TransactionChart from '../../components/TransactionChart';
import TabButton from '../../components/TabButton';
import useTransactionCount from './useTransactionCount';
import styles from './TransactionsCard.module.scss';
import { formatInTimeZone } from 'date-fns-tz';
import { Title } from 'UI';
import Throbber from '../../components/Throbber';

const cn = withStyles(styles);

type Tab = 'weekly' | 'monthly';

type TransactionsCardProps = {
  currency: 'CAD' | 'USD';
};

const TransactionsCard: FC<TransactionsCardProps> = ({ currency }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { fetching, fetchTransactionCount } = useTransactionCount();

  const [data, setData] = useState({
    weekly: { transactions: {}, count: 0 },
    monthly: { transactions: {}, count: 0 },
  });

  const [activeTab, setActiveTab] = useState<Tab>('weekly');
  const primaryColor = getCSSVariable('--primaryColor');
  const primaryHexWithOpacityBorder = `${primaryColor}80`;
  const primaryHexWithOpacityBackground = `${primaryColor}1A`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const endDate = formatInTimeZone(now, 'UTC', 'yyyy-MM-dd');

        const [weeklyRes, monthlyRes] = await Promise.all([
          fetchTransactionCount({
            Currency: currency,
            StartDateTime: formatInTimeZone(subDays(now, 6), 'UTC', 'yyyy-MM-dd'),
            EndDateTime: endDate,
          }),
          fetchTransactionCount({
            Currency: currency,
            StartDateTime: formatInTimeZone(subMonths(now, 1), 'UTC', 'yyyy-MM-dd'),
            EndDateTime: endDate,
          }),
        ]);
        setData({
          weekly: {
            transactions: weeklyRes.CountData,
            count: Object.values(weeklyRes.CountData).reduce((sum, val) => sum + val, 0),
          },
          monthly: {
            transactions: monthlyRes.CountData,
            count: Object.values(monthlyRes.CountData).reduce((sum, val) => sum + val, 0),
          },
        });
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
  }, [currency]);

  const options: { label: string; value: Tab }[] = [
    { label: t('Widgets.Transactions.weekly'), value: 'weekly' },
    { label: t('Widgets.Transactions.monthly'), value: 'monthly' },
  ];

  return (
    <>
      <Box ai="center" jc="sb" mb="md" height="auto">
        <Title level="5">{t('Widgets.Transactions.title')}</Title>
        <TabButton
          options={options}
          value={activeTab}
          disabled={fetching}
          onSelect={(val: Tab) => {
            setActiveTab(val);
          }}
        />
      </Box>

      <div style={{ position: 'relative' }}>
        <TransactionChart
          data={Object.entries(activeTab === 'weekly' ? data.weekly.transactions : data.monthly.transactions).map(
            ([date, count]) => ({
              label: format(parseISO(date), 'MMM d'),
              value: count as number,
            }),
          )}
          options={{
            label: t('Widgets.Transactions.label'),
            fill: true,
            tension: 0.2,
            ...(activeTab === 'monthly' && { tickSpace: 7 }),
          }}
        />

        <div className={cn('transactionBottomCardContainer')}>
          {options.map((opt) => {
            const isActive = activeTab === opt.value;

            return (
              <button
                key={opt.value}
                className={cn('transactionBottomCard')}
                {...(isActive && {
                  style: {
                    border: `2px solid ${primaryHexWithOpacityBorder}`,
                    backgroundColor: primaryHexWithOpacityBackground,
                  },
                })}
                onClick={() => {
                  setActiveTab(opt.value);
                }}
              >
                {opt.label}
                <h4 style={{ fontWeight: isActive ? 600 : 400 }}>
                  {opt.value === 'weekly' ? data.weekly.count : data.monthly.count}{' '}
                  {t('Widgets.Transactions.transactions', { numberOfTransactions: opt.label })}
                </h4>
              </button>
            );
          })}
        </div>

        {fetching && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#ffffffaa',
            }}
          >
            <Throbber />
          </div>
        )}
      </div>
    </>
  );
};

export default TransactionsCard;
